import axios from '@/plugins/axios';
import utils from '@/stores/utils';

const DEFAULT_LINE_COUNT = 3;

function emptyLine() {
    return {
        product_id: null,
        product: null,
        quantity: null,
        max_quantity: null,
        max_quantity_loading: false,
        errors: {},
    };
}

function initialLines(count = DEFAULT_LINE_COUNT) {
    return Array.from({ length: count }, () => emptyLine());
}

function pickOtherStock(stocks, sourceStockId) {
    if (!sourceStockId || stocks.length !== 2) {
        return null;
    }
    const other = stocks.find((stock) => stock.id !== sourceStockId);
    return other?.id ?? null;
}

const state = {
    source_storage_location: null,
    destination_storage_location: null,
    source_stock: null,
    destination_stock: null,
    comment: null,
    lines: initialLines(),
    stocks: [],
    stocks_loading: false,
    saving: false,
    saving_errors: {},
};

const mutations = {
    reset(state) {
        state.source_storage_location = null;
        state.destination_storage_location = null;
        state.source_stock = null;
        state.destination_stock = null;
        state.comment = null;
        state.lines = initialLines();
        state.saving = false;
        state.saving_errors = {};
    },
    updateSourceStorageLocation(state, id) { state.source_storage_location = id; },
    updateDestinationStorageLocation(state, id) { state.destination_storage_location = id; },
    updateSourceStock(state, stockId) {
        state.source_storage_location = null;
        state.source_stock = stockId;
        state.lines.forEach((line) => {
            if (!line.product_id) {
                return;
            }
            line.max_quantity = null;
            line.max_quantity_loading = true;
            line.errors = {};
        });
    },
    setSourceStock(state, stockId) {
        state.source_stock = stockId;
    },
    setLines(state, lines) {
        state.lines = lines;
    },
    addLine(state) {
        state.lines.push(emptyLine());
    },
    removeLine(state, index) {
        if (state.lines.length <= 1) {
            return;
        }
        state.lines.splice(index, 1);
    },
    updateDestinationStock(state, stockId) {
        state.destination_storage_location = null;
        state.destination_stock = stockId;
    },
    updateComment(state, comment) {
        state.comment = comment;
    },
    updateStocks(state, stocks) {
        state.stocks = stocks;
    },
    updateStocksLoading(state, loading) {
        state.stocks_loading = loading;
    },
    updateLineProduct(state, { index, productId, product }) {
        const line = state.lines[index];
        if (!line) {
            return;
        }
        line.product_id = productId;
        line.product = product || null;
        line.quantity = null;
        line.max_quantity = null;
        line.errors = {};
    },
    updateLineQuantity(state, { index, quantity }) {
        const line = state.lines[index];
        if (!line) {
            return;
        }
        line.quantity = quantity;
    },
    setAllQuantitiesToMax(state) {
        state.lines.forEach((line) => {
            if (
                line.product_id
                && !line.max_quantity_loading
                && line.max_quantity > 0
            ) {
                line.quantity = line.max_quantity;
            }
        });
    },
    updateLineMaxQuantity(state, { index, maxQuantity }) {
        const line = state.lines[index];
        if (!line) {
            return;
        }
        line.max_quantity = maxQuantity;
        line.max_quantity_loading = false;
        if (line.quantity != null && maxQuantity != null) {
            if (maxQuantity === 0) {
                line.quantity = null;
            } else if (line.quantity > maxQuantity) {
                line.quantity = maxQuantity;
            }
        }
    },
    updateLineMaxQuantityLoading(state, { index, loading }) {
        const line = state.lines[index];
        if (!line) {
            return;
        }
        line.max_quantity_loading = loading;
    },
    updateLineErrors(state, { index, errors }) {
        const line = state.lines[index];
        if (!line) {
            return;
        }
        line.errors = errors || {};
    },
    updateSaving(state, saving) {
        state.saving = saving;
        if (saving) {
            state.saving_errors = {};
        }
    },
    updateSavingErrors(state, errors) {
        state.saving = false;
        state.saving_errors = errors || {};
    },
};

const actions = {
    async fetchStocks({ commit }) {
        commit('updateStocksLoading', true);
        try {
            const response = await axios.get('/api/stocks/', {
                params: {
                    limit: 1000,
                    owned: true,
                },
            });
            const stocks = (response.data?.results || []).filter(
                (stock) => stock.track_stock !== false && stock.owned !== false,
            );
            commit('updateStocks', stocks);
            return stocks;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            throw error;
        } finally {
            commit('updateStocksLoading', false);
        }
    },

    async fetchLineMaxQuantity({ commit, state }, index) {
        const line = state.lines[index];
        if (!line?.product_id || !state.source_stock) {
            commit('updateLineMaxQuantity', { index, maxQuantity: null });
            return;
        }
        commit('updateLineMaxQuantityLoading', { index, loading: true });
        try {
            const response = await axios.get('/api/stockproducts/', {
                params: {
                    product: line.product_id,
                    stock: state.source_stock,
                    limit: 1,
                },
            });
            const row = response.data?.results?.[0];
            commit('updateLineMaxQuantity', {
                index,
                maxQuantity: state.source_storage_location ? (row?.location_balances?.find(balance => balance.storage_location === state.source_storage_location)?.count ?? 0) : (row != null ? row.count : 0),
            });
        } catch (xhr_error) {
            commit('updateLineMaxQuantity', { index, maxQuantity: null });
        }
    },

    async refreshLinesMaxQuantities({ dispatch, state }) {
        const indicesToFetch = state.lines
            .map((line, index) => (line.product_id ? index : null))
            .filter((index) => index !== null);
        await Promise.all(
            indicesToFetch.map((index) => dispatch('fetchLineMaxQuantity', index)),
        );
    },

    async changeSourceStock({ commit, dispatch }, stockId) {
        commit('updateSourceStock', stockId);
        await dispatch('refreshLinesMaxQuantities');
    },

    async init({ commit, dispatch, state }, params = {}) {
        commit('reset');
        const stocks = await dispatch('fetchStocks');

        const stockIds = new Set(stocks.map((stock) => stock.id));

        let sourceStockId = params.source_stock ?? null;
        if (sourceStockId && !stockIds.has(sourceStockId)) {
            sourceStockId = null;
        }
        if (!sourceStockId && stocks.length) {
            sourceStockId = stocks[0].id;
        }
        if (sourceStockId) {
            commit('setSourceStock', sourceStockId);
        }

        let destinationStockId = params.destination_stock ?? null;
        if (destinationStockId && !stockIds.has(destinationStockId)) {
            destinationStockId = null;
        }
        if (destinationStockId) {
            commit('updateDestinationStock', destinationStockId);
        } else {
            const autoDestination = pickOtherStock(stocks, sourceStockId);
            if (autoDestination) {
                commit('updateDestinationStock', autoDestination);
            }
        }

        if (params.products?.length) {
            const lines = params.products.map((product) => ({
                ...emptyLine(),
                product_id: product.id,
                product,
                quantity: product.quantity ?? null,
            }));
            const targetCount = Math.max(DEFAULT_LINE_COUNT, lines.length);
            while (lines.length < targetCount) {
                lines.push(emptyLine());
            }
            commit('setLines', lines);
        }

        await dispatch('refreshLinesMaxQuantities');
    },

    async saveTransfer({ commit, dispatch, state }) {
        commit('updateSaving', true);
        const items = state.lines
            .filter((line) => line.product_id && line.quantity > 0)
            .map((line) => ({
                product: line.product_id,
                quantity: line.quantity,
            }));

        const payload = {
            source_stock: state.source_stock,
            source_storage_location: state.source_storage_location,
            destination_storage_location: state.destination_storage_location,
            destination_stock: state.destination_stock,
            items,
        };
        if (state.comment) {
            payload.comment = state.comment;
        }

        try {
            const response = await axios.post('/api/stock-transfers/', payload);
            commit('updateSaving', false);
            dispatch('session/fetchStats', null, { root: true });
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit('updateSavingErrors', error.details);
            throw error;
        }
    },
};

export default {
    namespaced: true,
    state,
    actions,
    mutations,
};
